import { html, useEffect, useState } from '/vendor/htm-preact.module.js';

import { Icon } from '/icons.js';
import { parse, path, pageTag } from '/router.js';

/**
 * Pages somebody chose to keep — GitHub issue #77.
 *
 * Ben asked for "an option to add a shortcut to get to that page on the home
 * screen". This app has a lot of places (a league has seven rooms, a venue
 * four) and the route to the one you actually use every week is four taps
 * down. A shortcut is that route, kept.
 *
 * A HASH IS THE WHOLE SHORTCUT. Every place in this app is already a URL —
 * that was the point of routing rooms in issue #25 — so nothing here needs to
 * know what a league or a venue is. It stores the address and puts it back in
 * the address bar, which means a screen added tomorrow can be bookmarked
 * tomorrow, with nothing added here.
 *
 * ON THE DEVICE, not the account, and deliberately: this is the same class of
 * thing as the text size and the layout width, which are also per-device. The
 * phone in your pocket and the tablet on the pub wall want different
 * shortcuts, and syncing them would make one of the two wrong.
 *
 * NAMED WHEN IT IS ADDED, not when it is shown. The label is the heading that
 * was on screen at the time, because that is the only thing that says WHICH
 * league — `pageTag` deliberately carries no ids, so two leagues' Match Hubs
 * are both `LEAGUE/HUB` and a list of those is a list of identical rows. The
 * tag is kept beside the name for exactly the case where the names collide
 * too. A stored name can go stale if the league is renamed; that is a label
 * being out of date, which is visible and fixable, rather than a shortcut
 * pointing at the wrong place, which is not.
 */
const KEY = 'cueforge.shortcuts';

// Enough for the rooms somebody actually lives in, few enough that the home
// screen is still a home screen. Adding past the cap drops the oldest.
export const LIMIT = 8;

// Long enough for "Basingstoke and Deane Invitational Pool League", short
// enough that one shortcut cannot take the whole screen.
const MAX_NAME = 48;

export function shortcuts() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
    // Anything that is not a usable row is dropped rather than rendered: this
    // is parsed from storage the user's own browser hands us, and a shortcut
    // with no hash is a button that goes nowhere.
    return Array.isArray(raw)
      ? raw.filter((s) => s && typeof s.hash === 'string'
                   && s.hash.startsWith('#/'))
      : [];
  } catch (e) { return []; }
}

function write(list) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* fine */ }
}

/**
 * What to call the page you are on.
 *
 * The screens put their subject in a heading — `league-title`, `venue-title`
 * — so that is asked for first and by testid, which is the stable name in
 * this codebase. `h2`/`h1` is the fallback for a screen that has a heading
 * but no testid on it, and the tag is the fallback for one with no heading at
 * all (Home, Settings).
 */
export function nameHere(doc = document, hash = location.hash) {
  const found = doc.querySelector('[data-testid$="-title"], h1, h2');
  const text = (found && found.textContent || '').trim().replace(/\s+/g, ' ');
  return text ? text.slice(0, MAX_NAME) : pageTag(parse(hash));
}

/**
 * Can this page be kept at all?
 *
 * A shortcut is reopened by asking the router to go where the hash says, and
 * two screens carry something the hash cannot round-trip: a watch link and a
 * queue QR both hold a TOKEN that `path()` does not put back. Storing one
 * would produce a button that goes to a stripped URL — which is worse than
 * no button, because it looks like it worked.
 *
 * Asked as a round trip rather than as a list of screens to exclude: a
 * screen added tomorrow with the same shape is excluded tomorrow, and one
 * that gains a proper URL stops being excluded the day it does.
 */
export function canKeep(hash = location.hash) {
  if (!hash.startsWith('#/')) return false;
  const r = parse(hash);
  // IT HAS TO NAME SOMETHING. A shortcut shortens a journey, and the pages
  // whose address carries no id are the ones that are already one tap away:
  // the four tabs are permanently on screen, and Settings, Notifications and
  // Support each have their own button in the chrome. Offering a shortcut to
  // Home, on the home screen, under a list of shortcuts, is incoherent.
  //
  // A rule about what is WORTH keeping rather than what is possible, so it is
  // stated separately from the round trip below even though one gate answers
  // both.
  if (!r.id) return false;
  return path(r.screen, r.id, r.view, r.sub) === hash;
}

/** Is this page already kept? Compared on the hash, which IS the identity. */
export function isKept(hash = location.hash, list = shortcuts()) {
  return list.some((s) => s.hash === hash);
}

export function keep(hash = location.hash, name = null) {
  if (!canKeep(hash)) return shortcuts();
  const list = shortcuts().filter((s) => s.hash !== hash);
  list.push({ hash, name: name || nameHere(document, hash),
              tag: pageTag(parse(hash)) });
  // Oldest first out. `slice` from the END keeps what was added most
  // recently, which is what somebody who just pressed the button expects.
  const kept = list.slice(-LIMIT);
  write(kept);
  return kept;
}

export function drop(hash) {
  const kept = shortcuts().filter((s) => s.hash !== hash);
  write(kept);
  return kept;
}

/**
 * The star: keep this page, or give it back.
 *
 * IT GOES IN EACH SCREEN'S OWN CONTROL ROW, and that is not the arrangement
 * this started as. The obvious design is one control in the chrome, rendered
 * once — app.js argues exactly that about the page tag, and it is right about
 * the page tag. It does not work here: `Shell` is only drawn for the four
 * tabs and the three screens reached from the chrome. A league, a fixture, a
 * team and a cup each take the whole window and draw their own header, and
 * those are precisely the pages somebody wants a shortcut TO. A control in
 * the Shell would have appeared on every page nobody needs it on.
 *
 * So it is placed by hand, beside the Back button that is already there, and
 * `variant` is which of the two button shapes that row uses: a `ball` in the
 * brand lockup, a plain secondary in a screen's own row.
 *
 * Absent, not disabled, where a page cannot be kept: a greyed star invites a
 * press and then explains nothing.
 */
export function KeepPage({ variant = 'ball' }) {
  // Its own hash, not a prop: the Shell is handed `screen`, which does not
  // change when a league moves between its rooms — and a room is exactly the
  // kind of page worth keeping. Watched the way app.js watches it, including
  // the re-read on attach its comment explains: the state is seeded during
  // the first render and a hash that changes before the listener goes on
  // would otherwise leave a stale star.
  const [hash, setHash] = useState(() => location.hash || '#/');
  const [kept, setKept] = useState(() => isKept(location.hash || '#/'));
  useEffect(() => {
    const onHash = () => {
      const now = location.hash || '#/';
      setHash(now);
      setKept(isKept(now));
    };
    onHash();
    addEventListener('hashchange', onHash);
    return () => removeEventListener('hashchange', onHash);
  }, []);

  if (!canKeep(hash)) return null;
  const label = kept ? 'On your home screen' : 'Add to home screen';
  const ball = variant === 'ball';
  return html`
    <button class=${ball ? 'ball' : 'secondary'} data-testid="brand-keep"
            style=${ball ? 'color:var(--bg)' : 'width:auto'}
            aria-pressed=${kept} title=${label} aria-label=${label}
            onClick=${() => {
              if (kept) drop(hash); else keep(hash);
              setKept(!kept);
            }}>
      <${Icon} name="star" size=${16} />
    </button>`;
}

/**
 * The list, on the home screen between the ratings and Start a match —
 * which is where Ben asked for it, and is the right place: it is the row of
 * "take me back to where I was", and it sits under the thing you came to
 * look at and above the thing you came to do.
 *
 * Draws nothing at all until something is kept. An empty box explaining a
 * feature is a feature nobody has used taking up the space of one they have.
 */
export function Shortcuts({ onGo }) {
  const [list, setList] = useState(shortcuts);
  if (list.length === 0) return null;
  return html`
    <div class="stack" data-testid="home-shortcuts">
      ${list.map((s) => html`
        <div class="card rowbetween" key=${s.hash}
             data-testid="home-shortcut">
          ${/* Routed through `go` rather than by assigning `location.hash`:
                app.js moves the screen and the URL in the same tick on
                purpose, and setting the hash alone brings back the flash of
                the old screen its comment is about. `parse` is the inverse
                of the `path` that built this, and `canKeep` refused to
                store anything where it is not. */''}
          <button class="linky" data-testid="home-shortcut-open"
                  onClick=${() => {
                    const r = parse(s.hash);
                    onGo(r.screen, r.id, r.view, r.sub);
                  }}>
            ${s.name}
          </button>
          <span class="row" style="width:auto">
            <span class="sub mono" style="font-size:.7em">${s.tag}</span>
            <button class="secondary" style="width:auto"
                    data-testid="home-shortcut-drop"
                    aria-label=${`Remove ${s.name}`}
                    onClick=${() => setList(drop(s.hash))}>
              <${Icon} name="x" size=${14} />
            </button>
          </span>
        </div>`)}
    </div>`;
}
